import { Marker } from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { memo, useMemo, useState } from 'react';
import {
  RiCompasses2Line,
  RiSpeedUpLine,
  RiCameraLine,
  RiDvdLine,
  RiVipDiamondLine,
  RiGitPullRequestLine,
  RiTimerLine,
  RiRestartLine,
  RiArrowUpDownLine,
  RiPinDistanceLine,
  RiRemoteControlLine,
  RiToolsLine,
  RiListSettingsLine,
  RiToggleLine,
  RiFocus3Line,
  RiExchangeLine,
} from 'react-icons/ri';
import { useSelector } from 'react-redux';

import CondDistance from './CondDistance';
import CondYaw from './CondYaw';
import DoAutotuneEnable from './DoAutotuneEnable';
import DoChangeSpeed from './DoChangeSpeed';
import DoGripper from './DoGripper';
import DoGuidedLimits from './DoGuidedLimits';
import DoJump from './DoJump';
import DoParachute from './DoParachute';
import DoRepeatRelay from './DoRepeatRelay';
import DoRepeatServo from './DoRepeatServo';
import DoSetCamTriggDist from './DoSetCamTriggDist';
import DoSetRelay from './DoSetRelay';
import DoSetRoiLocation from './DoSetRoiLocation';
import DoSetRoiNone from './DoSetRoiNone';
import DoSetServo from './DoSetServo';
import DoVtolTransition from './DoVtolTransition';
import styles from './index.module.scss';
import NavContinueAndChangeAlt from './NavContinueAndChangeAlt';
import NavDelay from './NavDelay';
import NavGuidedEnable from './NavGuidedEnable';
import NavLand from './NavLand';
import NavLoiterTime from './NavLoiterTime';
import NavLoiterToAlt from './NavLoiterToAlt';
import NavLoiterTurns from './NavLoiterTurns';
import NavReturnToLaunch from './NavReturnToLaunch';
import NavTakeoff from './NavTakeoff';
import NavWaypoint from './NavWaypoint';

import AccordionCaret from '@/components/ui/AccordionCaret';

const cx = classNames.bind(styles);

const Item = ({ label, isCurrent, data: command }) => {
  const robots = useSelector((state) => state.robots);
  const targetRobotIds = useSelector((state) => state.robot.targetRobotIds);
  const [isOpen, setIsOpen] = useState(false);

  const Icon = useMemo(() => {
    // prettier-ignore
    switch (command.type) {
      case 'condDistance':            return RiPinDistanceLine;
      case 'condYaw':                 return RiRestartLine;
      case 'doAutotuneEnable':        return RiToolsLine;
      case 'doChangeSpeed':           return RiSpeedUpLine;
      case 'doGripper':               return RiCompasses2Line;
      case 'doGuidedLimits':          return RiListSettingsLine;
      case 'doJump':                  return RiGitPullRequestLine;
      case 'doParachute':             return RiVipDiamondLine;
      case 'doSetCamTriggDist':       return RiCameraLine;
      case 'doRepeatRelay':
      case 'doSetRelay':              return RiToggleLine;
      case 'doSetRoiNone':            return RiFocus3Line;
      case 'doRepeatServo':
      case 'doSetServo':              return RiDvdLine;
      case 'doVtolTransition':        return RiExchangeLine;
      case 'navContinueAndChangeAlt': return RiArrowUpDownLine;
      case 'navDelay':                return RiTimerLine;
      case 'navGuidedEnable':         return RiRemoteControlLine;

      // Use default marker icon below for these types.
      case 'doSetRoiLocation':
      case 'navLand':
      case 'navLoiterTime':
      case 'navLoiterToAlt':
      case 'navLoiterTurns':
      case 'navReturnToLaunch':
      case 'navTakeoff':
      case 'navWaypoint':
      default:
        return;
    }
  }, [command.type]);

  const title = useMemo(() => {
    // prettier-ignore
    switch (command.type) {
      case 'condDistance':            return 'Wait For Distance';
      case 'condYaw':                 return 'Wait For Yaw';
      case 'doAutotuneEnable':
        switch (command.data.enable) {
          case 0:                     return 'Autotune Disable';
          case 1:                     return 'Autotune Enable';
          default:
            return;
        }
      case 'doChangeSpeed':           return 'Change Speed';
      case 'doGripper':               return 'Gripper';
      case 'doGuidedLimits':          return 'Guided Limits';
      case 'doJump':                  return 'Jump To Item';
      case 'doParachute':
        switch (command.data.action) {
          case 0:                     return 'Parachute Disable';
          case 1:                     return 'Parachute Enable';
          case 2:                     return 'Parachute Release';
          default:
            return;
        }
      case 'doRepeatRelay':           return 'Cycle Relay';
      case 'doSetRoiLocation':        return 'Region Of Interest';
      case 'doSetRoiNone':            return 'Cancel ROI';
      case 'doRepeatServo':           return 'Cycle Servo';
      case 'doSetCamTriggDist':       return 'Set Camera Trigger Distance';
      case 'doSetRelay':              return 'Set Relay';
      case 'doSetServo':              return 'Set Servo';
      case 'doVtolTransition':        return 'VTOL Transition';
      case 'navContinueAndChangeAlt': return 'Change Altitude';
      case 'navDelay':                return 'Delay Until';
      case 'navGuidedEnable':
        switch (command.data.enable) {
          case 0:                     return 'Guided Mode Disable';
          case 1:                     return 'Guided Mode Enable';
          default:
            return;
        }
      case 'navLand':                 return 'Land';
      case 'navLoiterTime':           return 'Loiter (Time)';
      case 'navLoiterToAlt':          return 'Loiter (Altitude)';
      case 'navLoiterTurns':          return 'Loiter (Turns)';
      case 'navReturnToLaunch':       return 'Return To Launch';
      case 'navTakeoff':              return 'Takeoff';
      case 'navWaypoint':             return 'Waypoint';
      default:
        return;
    }
  }, [command.type]);

  useUpdateEffect(() => {
    setIsOpen(isCurrent);
  }, [isCurrent]);

  const toggle = (e) => {
    const isSelect = e.target.closest('select');
    if (isSelect) return;

    setIsOpen(!isOpen);
  };

  const color = useMemo(() => {
    return robots.find(({ id }) => id === targetRobotIds[0]).color;
  }, [robots, targetRobotIds]);

  return (
    <div className={cx('container')}>
      <div className={cx('header')} onClick={toggle}>
        <div className={cx('left')}>
          {!Icon && (
            <div className={cx('marker')}>
              <Marker color={color} label={label} />
            </div>
          )}
          {Icon && (
            <div className={cx('icon')}>
              <Icon size={18} color="white" />
            </div>
          )}
          <div className={cx('title')}>{title}</div>
        </div>
        <div className={cx('right')}>
          {isCurrent && <div className={cx('current')}>Current</div>}
          <AccordionCaret up={isOpen} size={32} />
        </div>
      </div>
      <div className={cx(['body', { open: isOpen }])}>
        <div className={cx('inner')}>
          {command.type === 'condDistance' && <CondDistance data={command} />}
          {command.type === 'condYaw' && <CondYaw data={command} />}
          {command.type === 'doAutotuneEnable' && <DoAutotuneEnable data={command} />}
          {command.type === 'doChangeSpeed' && <DoChangeSpeed data={command} />}
          {command.type === 'doGripper' && <DoGripper data={command} />}
          {command.type === 'doGuidedLimits' && <DoGuidedLimits data={command} />}
          {command.type === 'doJump' && <DoJump data={command} />}
          {command.type === 'doParachute' && <DoParachute data={command} />}
          {command.type === 'doRepeatRelay' && <DoRepeatRelay data={command} />}
          {command.type === 'doRepeatServo' && <DoRepeatServo data={command} />}
          {command.type === 'doSetCamTriggDist' && <DoSetCamTriggDist data={command} />}
          {command.type === 'doSetRelay' && <DoSetRelay data={command} />}
          {command.type === 'doSetRoiLocation' && <DoSetRoiLocation data={command} />}
          {command.type === 'doSetRoiNone' && <DoSetRoiNone data={command} />}
          {command.type === 'doSetServo' && <DoSetServo data={command} />}
          {command.type === 'doVtolTransition' && <DoVtolTransition data={command} />}
          {command.type === 'navContinueAndChangeAlt' && <NavContinueAndChangeAlt data={command} />}
          {command.type === 'navDelay' && <NavDelay data={command} />}
          {command.type === 'navGuidedEnable' && <NavGuidedEnable data={command} />}
          {command.type === 'navLand' && <NavLand data={command} />}
          {command.type === 'navLoiterTime' && <NavLoiterTime data={command} />}
          {command.type === 'navLoiterToAlt' && <NavLoiterToAlt data={command} />}
          {command.type === 'navLoiterTurns' && <NavLoiterTurns data={command} />}
          {command.type === 'navReturnToLaunch' && <NavReturnToLaunch />}
          {command.type === 'navTakeoff' && <NavTakeoff data={command} />}
          {command.type === 'navWaypoint' && <NavWaypoint data={command} />}
        </div>
      </div>
    </div>
  );
};

export default memo(Item);
